import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FaBookmark, FaRegComment, FaHeart, FaShareAlt } from "react-icons/fa";
import BottomNavbar from "../components/BottomNavbar";
import Navbar from "../components/Navbar";
import useScrollToTop from "../components/useScrollToTop";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RootState } from "../redux/Store";
import { useSelector } from "react-redux";
import { database } from "../appWrite"; // Ensure you have Appwrite configured
import { fetchPosts } from "../redux/slices/AllPostSlice";
import { useAppDispatch } from "../redux/hooks";

const BlogPost: React.FC = () => {
  useScrollToTop();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const posts = useSelector((state: RootState) => state.AllPost.posts);
  const userProfile = useSelector((state: RootState) => state?.Profile[0]);
  const [searchParams] = useSearchParams();
  const Id = searchParams.get("id") || "";
  const currentPost = posts.find((post) => post.$id === Id);

  const [likes, setLikes] = useState<number>(45);
  const [comments, setComments] = useState<string[]>(
    currentPost?.comments || []
  );
  const [commentInput, setCommentInput] = useState<string>("");

  useEffect(() => {
    if (currentPost) {
      setComments(currentPost.comments || []);
    }
  }, [currentPost]);

  const addComment = async () => {
    if (!userProfile) {
      alert("You must be logged in to comment.");
      navigate("/auth");
    }
    if (!commentInput.trim() || !currentPost) return;

    const userFirstName = userProfile?.name || "Anonymous";
    const formattedComment = `${userFirstName} - ${commentInput}`;

    const updatedComments = [...comments, formattedComment];
    setComments(updatedComments);
    setCommentInput("");

    try {
      await database.updateDocument(
        "67ce8a4a0032e13c6368",
        "67d139a9000478a4ce8f",
        currentPost.$id,
        { comments: updatedComments }
      );
      dispatch(fetchPosts());
    } catch (error) {
      console.error("Error updating comments:", error);
    }
  };

  return (
    <>
      <Navbar current="blog" />
      <div className="max-w-3xl mx-auto p-6 py-28">
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-xl md:text-2xl font-bold text-gray-900 mb-4"
        >
          {currentPost?.title}
        </motion.h1>

        <div className="flex items-center justify-between text-gray-500 text-sm mb-4">
          <span>
            By <strong>{currentPost?.author}</strong> |{" "}
            {currentPost?.createdAt.slice(0, 10)}
          </span>
          <span className="flex items-center gap-2">
            <FaBookmark className="w-5 h-5 cursor-pointer hover:text-gray-800" />
            <FaShareAlt className="w-5 h-5 cursor-pointer hover:text-gray-800" />
          </span>
        </div>

        <motion.img
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7 }}
          src={currentPost?.image}
          alt="Blog banner"
          className="w-full h-64 object-cover rounded-lg shadow-md mb-6"
        />

        <p className="text-gray-700 leading-relaxed mb-4">
          {currentPost?.content
            .split("\n")
            .map((paragraph: string, index: any) =>
              /^[A-Z\s]+$/.test(paragraph) ||
              (/^[A-Za-z\s]+$/.test(paragraph) && !paragraph.endsWith(".")) ? (
                <h2 key={index} className="text-xl font-bold mt-6">
                  {paragraph}
                </h2>
              ) : (
                <span key={index} className="block mb-4">
                  {paragraph}
                </span>
              )
            )}
        </p>

        <div className="flex items-center gap-4 mt-6">
          <button
            className="flex items-center gap-2 text-red-500 hover:text-red-600"
            onClick={() => setLikes(likes + 1)}
          >
            <FaHeart className="w-6 h-6" /> {likes}
          </button>
          <button className="flex items-center gap-2 text-gray-700 hover:text-gray-900">
            <FaRegComment className="w-6 h-6" /> {comments.length}
          </button>
        </div>

        <div className="mt-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Comments</h2>
          <div className="mb-4 flex gap-2">
            <input
              type="text"
              className="flex-1 p-2 border rounded-lg"
              placeholder="Write a comment..."
              value={commentInput}
              onChange={(e) => setCommentInput(e.target.value)}
            />
            <button
              className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              onClick={addComment}
            >
              Post
            </button>
          </div>
          {comments.length > 0 && (
            <ul className="space-y-3">
              {comments.map((comment, index) => {
                const [firstName, message] = comment.split(" - ");
                return (
                  <li
                    key={index}
                    className="p-3 bg-gray-100 rounded-lg flex items-center gap-3"
                  >
                    <img
                      src="https://placehold.co/400"
                      alt="User"
                      className="w-10 h-10 rounded-full"
                    />
                    <div>
                      <strong>{firstName}</strong>
                      <p>{message}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
      <BottomNavbar current="blog" />
    </>
  );
};

export default BlogPost;
